"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RiArrowDownSLine, RiArrowUpSLine, RiCloseLine, RiSearchLine } from "react-icons/ri";
import ProviderAvatar from "@/components/provider-avatar";
import Input from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { queryClient } from "@/config/react-query";
import { useUser } from "@/contexts/user-provider";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { useProviders } from "@/hooks/use-providers";
import { useTypingStore } from "@/hooks/use-team-chat-realtime";
import { formatDate } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import type { TeamMessageSummary, TeamUnreadCounts } from "@/types/team-chat";
import { MessageBubble } from "./message-bubble";
import { MessageInput } from "./message-input";
import { isOptimisticConversationId, teamChatQueryKeys, useMarkAsRead, useTeamConversation, useTeamMessages } from "@/query/team-chat";
interface ConversationDetailProps {
  conversationId: string;
  isSearchOpen?: boolean;
  onSearchClose?: () => void;
}
function TypingIndicator(t0) {
  const $ = _c(14);
  const {
    conversationId
  } = t0;
  let t1;
  if ($[0] !== conversationId) {
    t1 = s => s.typing[conversationId];
    $[0] = conversationId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const typing = useTypingStore(t1);
  const {
    providerIdProviderMap
  } = useProviders();
  let t2;
  bb0: {
    if (!typing || typing.size === 0) {
      let t3;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = [];
        $[2] = t3;
      } else {
        t3 = $[2];
      }
      t2 = t3;
      break bb0;
    }
    let t3;
    if ($[3] !== providerIdProviderMap || $[4] !== typing) {
      t3 = Array.from(typing).map(id => providerIdProviderMap.get(id)).filter(Boolean);
      $[3] = providerIdProviderMap;
      $[4] = typing;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = t3;
  }
  const typingProviders = t2;
  if (typingProviders.length === 0) {
    return null;
  }
  const firstTyper = typingProviders[0];
  let t3;
  if ($[6] !== firstTyper) {
    t3 = firstTyper && <ProviderAvatar providerId={firstTyper.id} size={20} />;
    $[6] = firstTyper;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex items-center gap-1 rounded-2xl rounded-bl-sm bg-background-secondary px-3 py-2 shadow-sm"><div className="flex gap-0.5"><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-foreground-muted [animation-delay:0ms]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-foreground-muted [animation-delay:150ms]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-foreground-muted [animation-delay:300ms]" /></div></div>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = typingProviders.length === 1 ? `${firstTyper?.firstName} is typing` : `${typingProviders.length} people typing`;
  let t6;
  if ($[9] !== t5) {
    t6 = <span className="text-caption text-foreground-muted">{t5}</span>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t3 || $[12] !== t6) {
    t7 = <div className="flex items-center gap-2 px-3 py-1">{t3}{t4}{t6}</div>;
    $[11] = t3;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
export function ConversationDetail(t0) {
  const $ = _c(90);
  const {
    conversationId,
    isSearchOpen: isSearchOpenProp,
    onSearchClose
  } = t0;
  const {
    currentUser
  } = useUser();
  const {
    data: conversation
  } = useTeamConversation(conversationId);
  const {
    messages,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = useTeamMessages(conversationId);
  const markAsRead = useMarkAsRead(conversationId);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const clock = useClinicClock();
  conversation?.members;
  let map;
  if ($[0] !== conversation?.members) {
    map = new Map();
    conversation?.members?.forEach(m => {
      const name = `${m.firstName} ${m.lastName}`.trim();
      map.set(m.providerId, name);
      map.set(m.id, name);
    });
    $[0] = conversation?.members;
    $[1] = map;
  } else {
    map = $[1];
  }
  const memberNameMap = map;
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const isSearchOpen = isSearchOpenProp ?? false;
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMatchIndex, setSearchMatchIndex] = useState(0);
  const searchInputRef = useRef(null);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const messageRefs = useRef(t1);
  const prevMessageCountRef = useRef(messages.length);
  const hasInitializedRef = useRef(false);
  let t2;
  if ($[3] !== conversationId || $[4] !== markAsRead || $[5] !== messages.length) {
    t2 = () => {
      hasInitializedRef.current = false;
      queryClient.setQueryData(teamChatQueryKeys.unreadCounts(), old => {
        if (!old || !(conversationId in old)) {
          return old;
        }
        const updated = {
          ...old
        };
        delete updated[conversationId];
        return updated;
      });
      markAsRead.mutate();
      prevMessageCountRef.current = messages.length;
      requestAnimationFrame(() => {
        hasInitializedRef.current = true;
      });
    };
    $[3] = conversationId;
    $[4] = markAsRead;
    $[5] = messages.length;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== conversationId) {
    t3 = [conversationId];
    $[7] = conversationId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  useEffect(t2, t3);
  let t4;
  if ($[9] !== markAsRead || $[10] !== messages.length) {
    t4 = () => {
      if (!hasInitializedRef.current) {
        return;
      }
      if (messages.length > prevMessageCountRef.current) {
        markAsRead.mutate();
      }
      prevMessageCountRef.current = messages.length;
    };
    $[9] = markAsRead;
    $[10] = messages.length;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== messages.length) {
    t5 = [messages.length];
    $[12] = messages.length;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  if ($[14] !== messages) {
    t6 = [...messages].reverse();
    $[14] = messages;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const displayMessages = t6;
  let t7;
  bb0: {
    if (!searchQuery.trim()) {
      let t8;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = [];
        $[16] = t8;
      } else {
        t8 = $[16];
      }
      t7 = t8;
      break bb0;
    }
    let t8;
    if ($[17] !== displayMessages || $[18] !== searchQuery) {
      const q = searchQuery.toLowerCase();
      t8 = displayMessages.filter(msg => !msg.isDeleted && msg.content && msg.content.toLowerCase().includes(q));
      $[17] = displayMessages;
      $[18] = searchQuery;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    t7 = t8;
  }
  const searchMatches = t7;
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const container = scrollContainerRef.current;
      if (container) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            container.scrollTop = container.scrollHeight;
          });
        });
      }
    };
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const scrollToBottom = t8;
  const hasScrolledForConversationRef = useRef(null);
  let t10;
  let t9;
  if ($[21] !== conversationId || $[22] !== displayMessages.length || $[23] !== isSearchOpen) {
    t9 = () => {
      if (messagesEndRef.current && !isSearchOpen && displayMessages.length > 0 && hasScrolledForConversationRef.current !== conversationId) {
        hasScrolledForConversationRef.current = conversationId;
        messagesEndRef.current.scrollIntoView({
          behavior: "auto"
        });
      }
    };
    t10 = [conversationId, displayMessages.length, isSearchOpen];
    $[21] = conversationId;
    $[22] = displayMessages.length;
    $[23] = isSearchOpen;
    $[24] = t10;
    $[25] = t9;
  } else {
    t10 = $[24];
    t9 = $[25];
  }
  useEffect(t9, t10);
  const wasNearBottomRef = useRef(true);
  const prevDisplayCountRef = useRef(displayMessages.length);
  let t11;
  if ($[26] !== currentUser?.id || $[27] !== displayMessages || $[28] !== isSearchOpen) {
    t11 = () => {
      if (isSearchOpen || displayMessages.length === 0 || displayMessages.length <= prevDisplayCountRef.current) {
        prevDisplayCountRef.current = displayMessages.length;
        return;
      }
      const lastMsg = displayMessages[displayMessages.length - 1];
      const isFromCurrentUser = lastMsg?.sender.providerId === currentUser?.id;
      if (!isFromCurrentUser && wasNearBottomRef.current) {
        scrollToBottom();
      }
      prevDisplayCountRef.current = displayMessages.length;
    };
    $[26] = currentUser?.id;
    $[27] = displayMessages;
    $[28] = isSearchOpen;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const t12 = currentUser?.id;
  let t13;
  if ($[30] !== displayMessages || $[31] !== isSearchOpen || $[32] !== t12) {
    t13 = [displayMessages, t12, isSearchOpen, scrollToBottom];
    $[30] = displayMessages;
    $[31] = isSearchOpen;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  useEffect(t11, t13);
  let t14;
  let t15;
  if ($[34] !== searchMatchIndex || $[35] !== searchMatches) {
    t14 = () => {
      if (searchMatches.length === 0) {
        return;
      }
      const match = searchMatches[searchMatchIndex];
      if (!match) {
        return;
      }
      const el = messageRefs.current.get(match.id);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }
    };
    t15 = [searchMatchIndex, searchMatches];
    $[34] = searchMatchIndex;
    $[35] = searchMatches;
    $[36] = t14;
    $[37] = t15;
  } else {
    t14 = $[36];
    t15 = $[37];
  }
  useEffect(t14, t15);
  let t16;
  if ($[38] !== fetchNextPage || $[39] !== hasNextPage || $[40] !== isFetchingNextPage) {
    t16 = () => {
      const container_0 = scrollContainerRef.current;
      if (!container_0) {
        return;
      }
      if (hasNextPage && !isFetchingNextPage && container_0.scrollTop < 100) {
        fetchNextPage();
      }
      const distanceFromBottom = container_0.scrollHeight - container_0.scrollTop - container_0.clientHeight;
      wasNearBottomRef.current = distanceFromBottom < 100;
      setShowScrollToBottom(distanceFromBottom > 200);
    };
    $[38] = fetchNextPage;
    $[39] = hasNextPage;
    $[40] = isFetchingNextPage;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const handleScroll = t16;
  let t17;
  let t18;
  if ($[42] !== isSearchOpen) {
    t17 = () => {
      if (isSearchOpen) {
        setSearchQuery("");
        setSearchMatchIndex(0);
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    };
    t18 = [isSearchOpen];
    $[42] = isSearchOpen;
    $[43] = t17;
    $[44] = t18;
  } else {
    t17 = $[43];
    t18 = $[44];
  }
  useEffect(t17, t18);
  let t19;
  if ($[45] !== onSearchClose) {
    t19 = () => {
      setSearchQuery("");
      setSearchMatchIndex(0);
      onSearchClose?.();
    };
    $[45] = onSearchClose;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  const closeSearch = t19;
  let t20;
  if ($[47] !== searchMatches.length) {
    t20 = direction => {
      if (searchMatches.length === 0) {
        return;
      }
      setSearchMatchIndex(prev => {
        if (direction === "up") {
          return prev > 0 ? prev - 1 : searchMatches.length - 1;
        }
        return prev < searchMatches.length - 1 ? prev + 1 : 0;
      });
    };
    $[47] = searchMatches.length;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  const navigateSearch = t20;
  let t21;
  if ($[49] !== closeSearch || $[50] !== navigateSearch) {
    t21 = e => {
      if (e.key === "Enter") {
        e.preventDefault();
        navigateSearch(e.shiftKey ? "up" : "down");
      }
      if (e.key === "Escape") {
        closeSearch();
      }
    };
    $[49] = closeSearch;
    $[50] = navigateSearch;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  const handleSearchKeyDown = t21;
  const highlightedMessageId = searchMatches.length > 0 ? searchMatches[searchMatchIndex]?.id : null;
  let t22;
  if ($[52] !== closeSearch || $[53] !== handleSearchKeyDown || $[54] !== isSearchOpen || $[55] !== navigateSearch || $[56] !== searchMatchIndex || $[57] !== searchMatches.length || $[58] !== searchQuery) {
    t22 = isSearchOpen && <div className="flex items-center gap-2 border-b border-border px-3 py-1.5"><div className="min-w-0 flex-1"><Input ref={searchInputRef} icon={RiSearchLine} value={searchQuery} onChange={e_0 => {
          setSearchQuery(e_0.target.value);
          setSearchMatchIndex(0);
        }} onKeyDown={handleSearchKeyDown} placeholder="Search messages..." className="py-1.5" /></div>{searchQuery && <span className="shrink-0 text-caption text-foreground-muted">{searchMatches.length > 0 ? `${searchMatchIndex + 1}/${searchMatches.length}` : "0 results"}</span>}<div className="flex shrink-0 items-center"><button onClick={() => navigateSearch("up")} disabled={searchMatches.length === 0} className="rounded p-0.5 text-foreground-secondary hover:text-foreground disabled:opacity-30"><RiArrowUpSLine className="h-4 w-4" /></button><button onClick={() => navigateSearch("down")} disabled={searchMatches.length === 0} className="rounded p-0.5 text-foreground-secondary hover:text-foreground disabled:opacity-30"><RiArrowDownSLine className="h-4 w-4" /></button><button onClick={closeSearch} className="ml-1 rounded p-0.5 text-foreground-secondary hover:text-foreground"><RiCloseLine className="h-4 w-4" /></button></div></div>;
    $[52] = closeSearch;
    $[53] = handleSearchKeyDown;
    $[54] = isSearchOpen;
    $[55] = navigateSearch;
    $[56] = searchMatchIndex;
    $[57] = searchMatches.length;
    $[58] = searchQuery;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== hasNextPage) {
    t23 = hasNextPage && <div className="flex justify-center py-2"><span className="text-caption text-foreground-muted">Loading older messages...</span></div>;
    $[60] = hasNextPage;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== clock || $[63] !== conversationId || $[64] !== displayMessages || $[65] !== highlightedMessageId || $[66] !== isLoading || $[67] !== memberNameMap) {
    t24 = isLoading || isOptimisticConversationId(conversationId) ? <div className="flex flex-1 flex-col gap-4 px-1 py-4"><div className="flex items-start gap-2"><Skeleton className="h-8 w-8 shrink-0 rounded-full" /><div className="space-y-1.5"><Skeleton className="h-3 w-20" /><Skeleton className="h-16 w-48 rounded-lg" /></div></div><div className="flex items-start justify-end gap-2"><div className="space-y-1.5 text-right"><Skeleton className="ml-auto h-3 w-16" /><Skeleton className="ml-auto h-12 w-56 rounded-lg" /></div></div><div className="flex items-start gap-2"><Skeleton className="h-8 w-8 shrink-0 rounded-full" /><div className="space-y-1.5"><Skeleton className="h-3 w-24" /><Skeleton className="h-20 w-64 rounded-lg" /></div></div><div className="flex items-start justify-end gap-2"><div className="space-y-1.5 text-right"><Skeleton className="ml-auto h-3 w-16" /><Skeleton className="ml-auto h-10 w-40 rounded-lg" /></div></div></div> : displayMessages.length === 0 ? <div className="flex flex-1 items-center justify-center text-body text-foreground-secondary">No messages yet. Say hello!</div> : <>{displayMessages.map((msg_0, index) => {
        const prevMsg = index > 0 ? displayMessages[index - 1] : null;
        const nextMsg = index < displayMessages.length - 1 ? displayMessages[index + 1] : null;
        const isFirstInGroup = !prevMsg || prevMsg.sender.providerId !== msg_0.sender.providerId;
        const isLastInGroup = !nextMsg || nextMsg.sender.providerId !== msg_0.sender.providerId;
        const showDateSeparator = !prevMsg || !clock.isSameDay(prevMsg.createdDate, msg_0.createdDate);
        const isHighlighted = msg_0.id === highlightedMessageId;
        const msgWithListKey = msg_0 as TeamMessageSummary & {
          _listKey?: string;
        };
        const listKey = msgWithListKey._listKey ?? msg_0.id;
        return <div key={listKey} ref={el_0 => {
          if (el_0) {
            messageRefs.current.set(msg_0.id, el_0);
          }
        }}>{showDateSeparator && <div className="my-4 flex justify-center"><span className="rounded-full bg-background-secondary/80 px-3 py-0.5 text-caption text-foreground-muted shadow-sm">{formatDate(msg_0.createdDate, true, false, undefined, true, clock.timeZone)}</span></div>}<div className={cx("rounded-lg transition-colors duration-300", isHighlighted && "bg-status-warning-bg/50")}><MessageBubble message={msg_0} showSender={isFirstInGroup} showAvatar={isLastInGroup} memberNameMap={memberNameMap} /></div></div>;
      })}<div ref={messagesEndRef} /></>;
    $[62] = clock;
    $[63] = conversationId;
    $[64] = displayMessages;
    $[65] = highlightedMessageId;
    $[66] = isLoading;
    $[67] = memberNameMap;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] !== handleScroll || $[70] !== t23 || $[71] !== t24) {
    t25 = <div ref={scrollContainerRef} onScroll={handleScroll} className="flex h-full flex-col overflow-y-auto px-3 py-2">{t23}{t24}</div>;
    $[69] = handleScroll;
    $[70] = t23;
    $[71] = t24;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  let t26;
  if ($[73] !== showScrollToBottom) {
    t26 = showScrollToBottom && <button onClick={scrollToBottom} className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-background border border-border px-3 py-1 text-caption text-foreground-secondary shadow-md hover:bg-hover transition-all"><RiArrowDownSLine className="mr-0.5 inline-block h-4 w-4" />New messages</button>;
    $[73] = showScrollToBottom;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  let t27;
  if ($[75] !== t25 || $[76] !== t26) {
    t27 = <div className="relative flex-1 overflow-hidden">{t25}{t26}</div>;
    $[75] = t25;
    $[76] = t26;
    $[77] = t27;
  } else {
    t27 = $[77];
  }
  let t28;
  if ($[78] !== conversationId) {
    t28 = <TypingIndicator conversationId={conversationId} />;
    $[78] = conversationId;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  let t29;
  if ($[80] !== conversationId) {
    t29 = isOptimisticConversationId(conversationId);
    $[80] = conversationId;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  let t30;
  if ($[82] !== conversationId || $[83] !== t29) {
    t30 = <MessageInput conversationId={conversationId} onMessageSent={scrollToBottom} disabled={t29} />;
    $[82] = conversationId;
    $[83] = t29;
    $[84] = t30;
  } else {
    t30 = $[84];
  }
  let t31;
  if ($[85] !== t22 || $[86] !== t27 || $[87] !== t28 || $[88] !== t30) {
    t31 = <div className="flex min-h-0 flex-1 flex-col">{t22}{t27}{t28}{t30}</div>;
    $[85] = t22;
    $[86] = t27;
    $[87] = t28;
    $[88] = t30;
    $[89] = t31;
  } else {
    t31 = $[89];
  }
  return t31;
}